import { QuoteEmail } from "./email.model";
import { QuoteLocation } from "./quote-location.model";
import { Client } from "../../client/client.model";
import { BaseUser } from "../../user/base-user.model";
import { currencies } from "../../shared/currencies/currencies.const";
import { propertyToAccessorString } from "../../shared/helper/property-to-getter-string.function";
import { lclFormFields } from "../../shared/form/fields/lcl-form-fields.const";
import { locationKind } from "../../location/location-kind.const";
import { BaseStandardQuote } from "./quote-base-standard.model";
import { Package } from "../quote-form/quote-package/package.model";
import { LocalCostLabel } from "./local-cost-label.model";
import { Insurance } from "./insurance.model";
import { QuoteSurchargeType } from "./quote-surcharge-type.model";

export class BaseLclQuote extends BaseStandardQuote {
  protected showChargesInTotalCurrency = false;
  protected chargesCurrency: string = null;

  constructor(
    protected id: number,
    protected displayId: string,
    protected groupId: number,
    protected ref: string,
    protected sender: BaseUser,
    protected createdBy: BaseUser,
    protected status: string,
    protected quoteType: string,
    protected direction: string,
    protected incotermType: string,
    protected clients: Client[],
    protected hasOrigin: boolean,
    protected hasDestination: boolean,
    protected originLocations: QuoteLocation[],
    protected destinationLocations: QuoteLocation[],
    protected localCostLabels: LocalCostLabel[],
    protected createdAt: Date,
    protected updatedAt: Date,
    protected updatedBy: BaseUser,
    protected openedAt: Date,
    protected validFrom: Date,
    protected validTo: Date,
    protected weight: number,
    protected measurement: number,
    protected pieces: number,
    protected comment: string,
    protected showRateSurcharges = false,
    protected showCarrier = false,
    protected showInland = false,
    protected showTotal = false,
    protected totalCurrency = null,
    protected showInvoiceInfo = false,
    protected invoiceCurrency = false,
    protected groupRoutes = false,
    protected email: QuoteEmail,
    protected internalComment: string,
    protected customTerms: string,
    protected fileName: string,
    protected fileNameInternal: string,
    protected averageMargin: number,
    protected averageMarginPercent: number,
    protected inquiry: string,
    protected source: string,
    protected insurance: Insurance | null,
    protected reasonOfDecline: string,
    protected input: string | null,
    protected offerLanguage: string | null,
    protected groupOriginSurcharges = false,
    protected groupDestinationSurcharges = false,
    protected groupOriginSurchargesCurrency = currencies.usd.value,
    protected groupDestinationSurchargesCurrency = currencies.usd.value,
    protected packages: Package[],
    protected chargeableWeight: number,
    protected ldm: number,
  ) {
    super(
      id,
      displayId,
      groupId,
      ref,
      sender,
      createdBy,
      status,
      quoteType,
      clients,
      createdAt,
      updatedAt,
      updatedBy,
      openedAt,
      validFrom,
      validTo,
      email,
      internalComment,
      customTerms,
      fileName,
      fileNameInternal,
      averageMargin,
      inquiry,
      source,
      insurance,
      reasonOfDecline,
      input,
      offerLanguage,
      hasOrigin,
      hasDestination,
      originLocations,
      destinationLocations,
      localCostLabels,
    );
  }

  public getDirection(): string {
    return this.direction;
  }

  public setDirection(direction: string): void {
    this.direction = direction;
  }

  public getIncotermType(): string {
    return this.incotermType;
  }

  public setIncotermType(type: string): void {
    this.incotermType = type;
  }

  public getOriginLocations(): QuoteLocation[] {
    return this.originLocations;
  }

  public setOriginLocations(locations: QuoteLocation[]): void {
    this.originLocations = locations;
  }

  public getDestinationLocations(): QuoteLocation[] {
    return this.destinationLocations;
  }

  public setDestinationLocations(locations: QuoteLocation[]): void {
    this.destinationLocations = locations;
  }

  public setAverageMarginPercent(param: number): void {
    this.averageMarginPercent = param;
  }

  public getAverageMarginPercent(): number {
    return this.averageMarginPercent;
  }

  public getMinimalQuoteLclValue(key: string): number {
    const getter = propertyToAccessorString(key, "get");
    const quoteLclValue = this[getter]();
    const minimalValue = key === lclFormFields.weight ? 1000 : 1;

    return quoteLclValue < minimalValue ? minimalValue : quoteLclValue;
  }

  public getFieldValue(key: string): number {
    const getter = propertyToAccessorString(key, "get");
    if (this[getter]) {
      return this[getter]();
    }

    return 0;
  }

  public getWeight(): number {
    return this.weight;
  }

  public getWeightInTon(): number {
    return this.weight / 1000;
  }

  public setWeight(param: number) {
    this.weight = param;
  }

  public getChargeableWeight(): number {
    return this.chargeableWeight;
  }

  public setChargeableWeight(param: number) {
    this.chargeableWeight = param;
  }

  public getMeasurement(): number {
    return this.measurement;
  }
  public setMeasurement(param: number) {
    this.measurement = param;
  }

  public getWm(): number {
    return this.weight || this.measurement
      ? Math.max(this.weight, this.measurement)
      : 1;
  }

  public getComment(): string {
    return this.comment;
  }

  public setComment(comment: string) {
    this.comment = comment;
  }

  public getShowRateSurcharges(): boolean {
    return this.showRateSurcharges;
  }

  public setShowRateSurcharges(param: boolean) {
    this.showRateSurcharges = param;
  }

  public getShowCarrier(): boolean {
    return this.showCarrier;
  }

  public setShowCarrier(param: boolean) {
    this.showCarrier = param;
  }

  public getShowInland(): boolean {
    return this.showInland;
  }

  public setShowInland(param: boolean) {
    this.showInland = param;
  }

  public getShowTotal(): boolean {
    return this.showTotal;
  }

  public setShowTotal(param: boolean) {
    this.showTotal = param;
  }

  public getShowChargesInTotalCurrency(): boolean {
    return this.showChargesInTotalCurrency;
  }

  public setShowChargesInTotalCurrency(param: boolean) {
    this.showChargesInTotalCurrency = param;
  }

  public getChargesCurrency(): string {
    return this.chargesCurrency;
  }

  public setChargesCurrency(param: string) {
    this.chargesCurrency = param;
  }

  public getGroupRoutes(): boolean {
    return this.groupRoutes;
  }

  public setGroupRoutes(param: boolean) {
    this.groupRoutes = param;
  }

  public getTotalCurrency(): string {
    return this.totalCurrency;
  }

  public setTotalCurrency(param: string) {
    this.totalCurrency = param;
  }

  public getShowInvoiceInfo(): boolean {
    return this.showInvoiceInfo;
  }

  public setShowInvoiceInfo(param: boolean) {
    this.showInvoiceInfo = param;
  }

  public getInvoiceCurrency(): boolean {
    return this.invoiceCurrency;
  }

  public setInvoiceCurrency(param: boolean) {
    this.invoiceCurrency = param;
  }

  public getGroupOriginSurcharges(): boolean {
    return this.groupOriginSurcharges;
  }

  public setGroupOriginSurcharges(param: boolean) {
    this.groupOriginSurcharges = param;
  }

  public getGroupDestinationSurcharges(): boolean {
    return this.groupDestinationSurcharges;
  }

  public setGroupDestinationSurcharges(param: boolean) {
    this.groupDestinationSurcharges = param;
  }

  public getGroupOriginSurchargesCurrency(): string {
    return this.groupOriginSurchargesCurrency;
  }

  public setGroupOriginSurchargesCurrency(param: string) {
    this.groupOriginSurchargesCurrency = param;
  }

  public getGroupDestinationSurchargesCurrency(): string {
    return this.groupDestinationSurchargesCurrency;
  }

  public setGroupDestinationSurchargesCurrency(param: string) {
    this.groupDestinationSurchargesCurrency = param;
  }

  public getPieces(): number {
    return this.pieces || 0;
  }

  public setPieces(param: number) {
    this.pieces = param;
  }

  public getPackages(): Package[] {
    return this.packages;
  }

  public setPackages(param: Package[]) {
    this.packages = param;
  }

  public getLdm(): number {
    return this.ldm;
  }

  public setLdm(ldm: number): void {
    this.ldm = ldm;
  }

  public getQuoteSurchargeTypes(): QuoteSurchargeType[] {
    return [];
  }

  public setQuoteSurchargeTypes(
    _quoteSurchargeTypes: QuoteSurchargeType[],
  ): void {
    // Intentionally empty in base class; overridden by quote types that support it.
  }

  protected hasRequiredMetrics(): boolean {
    return this.getWeight() > 0 && this.getMeasurement() > 0;
  }

  public isValid(): boolean {
    return super.isValid() && this.hasRequiredMetrics();
  }

  public isSendable(): boolean {
    return super.isSendable() && this.hasRequiredMetrics();
  }

  public isAirQuote() {
    return this.getKind() === locationKind.airport;
  }
}
